/**
 * 图片组件
 *
 * 支持 COS 图片路径拼接、加载失败兜底、强制刷新等功能
 *
 * @module components/ui/image
 */
import { cn } from '@/lib/utils';
import React from 'react';

// 全局COS基础路径（从环境变量读取）
const COS_PATH = import.meta.env.VITE_COS_PATH ?? 'https://static.huashicloud.com/assets/ka/icua/image/';

// 简化类型定义：只继承原生img属性+扩展业务属性
interface CosImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  cosPath?: string;
  forceUpdate?: boolean;
  containerClassName?: string;
}

// 简化兜底图：直接用base64字符串
const FALLBACK_SRC = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAABC9JREFUWEfFl09oHHUUx7/vt5tNNxWrklv1UOLBok0FiyFFhE2TmVTRoOxONl4Ue1BoOtttxVSQBBUtQhOzsUWqRm9td7KIEoo72zbBS5CKkByq9VBTMDkUbLEkuM2f+T2ZjZNM0t3MTHLInnaX93vfz3u/75vf/Ahb/KEt1scyQN/5wj4Rxq6NAvGi9Uu64+DNoOtLAANG4Q0Gfxt08ap45jOp9tbOoDlKAJmseR6E7SlNfTloggHDjDNzlii851/wdBR8DeCdHnkmJejVtKaM/w+QN+wFqfZWLQgAM1NmqDAhgN90TU0OGIVuBh+FEG8zM1fKRcB7kBhPtStvrgvwhXFl5xysZwg8ndLUX9cmdFdfDZ6eg5wEqF/XlA/XKySTXSm4IkDGKBxjyJMEitjJCHSxOFPs6DrUNmP/rlR9EWLXCa3l7qYAMtl8DERXAHqH5he/RnVkL7NlgPFdql09vGTcpb23QqjfLkNTfqtf8pxHB/qz5pcE7Em1q41OJZ/nLqWl5O6Upjy8mep9AwjCU7qm7i8HUKoenLUE6udlaCoKOUk+9t7J5dmB9bZA15ROt/NPG5cbLcgLRVC91977Bii1qYIJa3bUKFJahl39sXjrtSBjGwjADi43hhnDHCeB63pcTW5E3JcH1h0ho3CVgBldUw5sCUB/zmwgiTGB0GtHtOZsOYgzxugDi7TwDZgbiOgTPaGcdcd5mtCrssxQ4SswHwwj8sRhLTbrjrfFFzCfJ6AOjCES1CmJjx+Nq58F9kBPT494aHfj03MidMPt8FPnhmurwpE/CGJQ11redRKviHOdlKGmdLLl9/6smRaCet0QvjrQZ4xFQ5j5HoACxi3iKlVPNk04YgNGvpOZ+oit3XryhRtucSGqY0fisetObH/OTAtegfAEKInT7DAYewVRQoLft7+TlM16snWitMdY+BHgxyMI75u9t/hP1TaYBK5bK14OgiwuPWHt0/e+w8gt7gi6/2PQKwQ+aYtLKZw2P0+En0DoSCXUC5U85HSCme+AeeQ+AIsefN2pnGS42d1yB4KAJma+5YgvG8swR4DSS03DeiZehpAytxpAUBSgqLvVa93ttH2t+Klzo7VV4fmrAG6mNLXJe4rMYUgurgIgIeLMuO203a94j2FEHsGOywAe3RaNPPvWS7G/PQHKHsdETST5gG0yv+J23MCQOciMeEjw/k6fZ4PnFLjnulLbl8QLx5n5UwK36VrrRa/KAz2I3KO2ds9L4kb+RQb9QERdekLp9Ste4TBa/VruJX46l3/SkjRGhJyeUA8FES8L4OtiQvRRKqF0nx0erb1XLDl+6g7uNn+gafObBrATeF3NrEX++XiH+ldmad5jBIww8+2g4nY8AzEhxKCeUE4EvpxmjHwvGI9tRNhZQ0L8WYzUfNzV9txMYIDNCJdb+x+CEgxO2zgCnAAAAABJRU5ErkJggg==';

export const CosImage: React.FC<CosImageProps> = ({
  cosPath = COS_PATH,
  src,
  forceUpdate = false,
  className,
  containerClassName,
  alt = '', // 给alt设置默认值，避免警告
  ...rest
}) => {
  // 核心：拼接图片链接
  const getImageSrc = () => {
    if (!src) return FALLBACK_SRC; // 无src直接返回兜底图
    // http开头的完整链接直接用
    if (/^https?:\/\//.test(src)) return src;
    // 拼接COS路径
    const fullSrc = cosPath ? `${cosPath}${src}` : src;
    // 强制刷新：加时间戳
    return forceUpdate ? `${fullSrc}?t=${Date.now()}` : fullSrc;
  };

  // 加载失败处理：直接切换为兜底图
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = FALLBACK_SRC;
    console.warn('图片加载失败，已显示兜底图：', src);
  };

  // 简化渲染逻辑：要么容器包裹，要么直接返回img
  const imageElement = (
    <img
      src={getImageSrc()}
      alt={alt}
      className={cn('object-cover', className)} // 加基础object-cover，避免图片变形
      loading="lazy" // 原生懒加载（必要）
      onError={handleError} // 失败兜底（必要）
      {...rest}
    />
  );

  // 有容器样式才包裹div，否则直接返回img
  return containerClassName ? (
    <div className={cn('relative', containerClassName)}>{imageElement}</div>
  ) : (
    imageElement
  );
};
